import { api } from "../config/variables";
import { shouldSendToService } from "./registry/eventRegistry";
import SERVICE_NAMES from "./constants/serviceConstants";
import { readCookie } from "../utils/functions";
import { isMobile } from "../utils/mobileDetection";

/**
 * Analytics Service Manager
 * Provides unified interface for all analytics services
 */
class AnalyticsManager {
	constructor() {
		this.services = {
			postHog: null,
			moEngage: null,
			gtm: null,
		};
		this.isInitialized = false;
	}

	/**
	 * Initialize all analytics services
	 */
	async initialize() {
		try {
			const importPromises = [];

			if (api.postHog?.isEnabled) {
				importPromises.push(
					import("./services/postHog").then(({ default: postHogService }) => {
						this.services.postHog = postHogService;
						postHogService.initialize();
						return postHogService;
					})
				);
			}

			if (api.moEngage?.isEnabled) {
				importPromises.push(
					import("./services/moEngage")
						.then(async ({ default: moEngageService }) => {
							this.services.moEngage = moEngageService;
							await moEngageService.initialize();
							return moEngageService;
						})
						.catch((error) => {
							console.error("Failed to load MoEngage service:", error);
							return null;
						})
				);
			}

			if (api.gtm?.isEnabled) {
				importPromises.push(
					import("./services/gtm")
						.then(({ default: gtmService }) => {
							this.services.gtm = gtmService;
							gtmService.initialize();
							return gtmService;
						})
						.catch((error) => {
							console.error("Failed to load GTM service:", error);
							return null;
						})
				);
			}

			await Promise.all(importPromises);
			this.isInitialized = true;
			console.log("Analytics Manager initialized successfully");
		} catch (error) {
			console.error("Failed to initialize Analytics Manager:", error);
		}
	}

	/**
	 * Track event based on event registry configuration
	 * @param {string} eventName - Name of the event
	 * @param {Object} properties - Event properties
	 * @param {Object} options - Additional options
	 */
	async trackEvent(eventName, properties = {}, options = {}) {
		if (!this.isInitialized) {
			console.warn("Analytics Manager not initialized");
			return;
		}
		const clientCookie = readCookie("client", true);

		const isServiceEligible = (serviceKey, serviceName, debugFlag) => {
			const service = this.services[serviceKey];
			const env = api.currentEnv;

			if (
				!(
					service &&
					service.isReady() &&
					shouldSendToService(eventName, serviceName)
				)
			) {
				return false;
			}
			if (env === "dev") {
				return !!debugFlag;
			}
			if (env === "staging" || env === "production") {
				return !clientCookie?.is_staff;
			}
			return false;
		};

		const isMoEngageEligible = () =>
			isServiceEligible(
				"moEngage",
				SERVICE_NAMES.MOENGAGE,
				api.moEngage?.debug
			);

		const isPostHogEligible = () =>
			isServiceEligible("postHog", SERVICE_NAMES.POSTHOG, api.postHog?.debug);

		const isGTMEligible = () =>
			isServiceEligible("gtm", SERVICE_NAMES.GOOGLEANALYTICS, api.gtm?.debug);

		if (isPostHogEligible()) {
			const eventProperties = {
				source: isMobile() ? "mweb" : "web",
				email: clientCookie?.email || "",
				company_id: clientCookie?.client_poc?.client?.sr_id || "",
				phone_number: clientCookie?.contact_no || "",
				...properties,
			};
			this.services.postHog.trackEvent(eventName, eventProperties, options);
		}

		if (isMoEngageEligible()) {
			const eventProperties = {
				source: isMobile() ? "mweb" : "web",
				email: clientCookie?.email || "",
				company_id: clientCookie?.client_poc?.client?.sr_id || "",
				phone_number: clientCookie?.contact_no || "",
				bu_name: "Cargo",
				is_first: "TRUE",
				...properties,
			};
			await this.services.moEngage.trackEvent(
				eventName,
				eventProperties,
				options
			);
		}

		if (isGTMEligible()) {
			const eventProperties = {
				source: isMobile() ? "mweb" : "web",
				email: clientCookie?.email || "",
				company_id: clientCookie?.client_poc?.client?.sr_id || "",
				phone_number: clientCookie?.contact_no || "",
				bu_name: "Cargo",
				...properties,
			};
			this.services.gtm.trackEvent(eventName, eventProperties, options);
		}
	}

	/**
	 * Get service status
	 * @returns {Object} Status of all services
	 */
	getServiceStatus() {
		return {
			postHog: this.services.postHog ? this.services.postHog.isReady() : false,
			moEngage: this.services.moEngage
				? this.services.moEngage.isReady()
				: false,
			gtm: this.services.gtm ? this.services.gtm.isReady() : false,
		};
	}

	/**
	 * Get detailed service loading status for debugging
	 * @returns {Object} Detailed status of all services
	 */
	getDetailedServiceStatus() {
		return {
			isInitialized: this.isInitialized,
			services: {
				postHog: {
					loaded: !!this.services.postHog,
					ready: this.services.postHog
						? this.services.postHog.isReady()
						: false,
					service: this.services.postHog,
				},
				moEngage: {
					loaded: !!this.services.moEngage,
					ready: this.services.moEngage
						? this.services.moEngage.isReady()
						: false,
					service: this.services.moEngage,
				},
				gtm: {
					loaded: !!this.services.gtm,
					ready: this.services.gtm ? this.services.gtm.isReady() : false,
					service: this.services.gtm,
				},
			},
		};
	}
}

export default new AnalyticsManager();
